import * as React from 'react';

import {assoc, emptyArray, emptyObject} from '@packages/utilities/fp/utils';
import {relative, within, type Box} from '@packages/utilities/box';
import {tokens} from '@packages/utilities/styled';
import {useOverlay, useViewport} from '@packages/systems/ui/overlay';
import useResizeObserver from '@packages/systems/ui/hooks/useResizeObserver';

import {type UseFloatingBaseProps} from '../../hooks';

import {createCutout} from './Mask.utils';

import type {
  HTMLAttributes,
  SpacingNumber,
  SpringTooling,
} from '../__SharedTypes';
import {View} from '../View';
import {SVGWrapper} from './Mask.styles';

type Cutout = Box | React.MutableRefObject<Element | null>;
export type CutoutProps = {
  borderRadius?: SpacingNumber | `${number}px`;
  cutout: Cutout;
};

type Cutouts = Array<CutoutProps>;
const SPACING_BASE_VALUE = 4;

export type MaskComponentProps = {
  /**
   * The border radius of the cutouts.
   */
  anchorBorderRadius?: CutoutProps['borderRadius'];
  /**
   * The anchor element that the Mask should cut out around.
   */
  anchorCutoutRef?: Cutout;
  /**
   * An array of transparent cutouts that allow interaction behind the Mask.
   */
  interactiveCutouts?: Cutouts;
  /**
   * Whether the anchor element should be interactive.
   */
  isAnchorInteractive?: boolean;
  /**
   * Whether the Mask should render.
   */
  isOpen?: boolean;
  /**
   * An array of transparent cutouts that disable interaction behind the Mask.
   */
  nonInteractiveCutouts?: Cutouts;
  /**
   * Callback function triggered when the Mask is clicked.
   */
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  /**
   * Whether the Mask allows pointer events to pass through.
   */
  shouldAllowPointerEvents?: boolean;
  /**
   * Custom inline styling for the Mask.
   */
  style?: React.CSSProperties;
  /**
   * Whether the Mask should escape its viewport's boundaries. When true, the Mask will use the overall window as its viewport.
   */
  shouldEscapeViewport?: boolean;
  /**
   * The color of the Mask.
   */
  type?: 'modal' | 'popover' | 'transparent';
} & HTMLAttributes &
  SpringTooling;

export type MaskPropsType = {
  /**
   * The border radius of the cutouts.
   */
  anchorBorderRadius?: CutoutProps['borderRadius'];
  /**
   * Color of the Mask.
   */
  colorVariant?: 'default' | 'transparent';
  /**
   * An array of transparent cutouts that allow interaction behind the Mask.
   */
  interactiveCutouts?: MaskComponentProps['interactiveCutouts'];
  /**
   * Whether the anchor element should be interactive.
   */
  isAnchorInteractive?: boolean;
  /**
   * An array of transparent cutouts that disable interaction behind the Mask.
   */
  nonInteractiveCutouts?: MaskComponentProps['nonInteractiveCutouts'];
  /**
   * Callback function triggered when the Mask is clicked.
   */
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  /**
   * Whether the Mask should escape its viewport and render in the window.
   */
  shouldEscapeViewport?: UseFloatingBaseProps['shouldEscapeViewport'];
};

/**
 * Converts a reference to an HTML input element into a box representing its position and dimensions
 * relative to the viewport.
 * @param ref - The reference to the HTML input element.
 * @returns A box object representing the position and dimensions of the input element relative to the viewport,
 * or null if the input element reference is invalid.
 */
const convertRefToBox = (
  ref: React.MutableRefObject<Element | null>
): Box | null => {
  const element = ref.current;

  if (!element) {
    return null;
  }

  const {top, left, width, height, x, y} = element.getBoundingClientRect();

  return {top, left, width, height, x, y};
};

/**
 * Converts an array of cutouts, which can be either represented as boxes or input element references,
 * into an array of boxes relative to the viewport using the provided function. Also applies borderRadius to the cutouts.
 * @param cutouts - An array containing objects of type CutoutProps
 * @param relativeToViewport - A function that takes a box and returns its position relative to the viewport.
 * @returns An array of converted cutouts represented as boxes relative to the viewport.
 */
const convertCutouts = (
  cutouts: Cutouts,
  relativeToViewport: (arg1: Box) => Box
): Box[] => {
  const convertedCutouts: Box[] = [];

  cutouts.forEach((cutoutDefinition) => {
    if (!cutoutDefinition) {
      return;
    }

    const {borderRadius, cutout} = cutoutDefinition;

    /**
     * If we have a DOM node, we'll get the border radius from the computed style unless an explicit borderRadius is requested.
     *
     * If an explicit borderRadius is provided, we will:
     *  - convert it to a number if it is a string
     *  - or multiply it by the SPACING_BASE_VALUE.
     */
    const cutoutBorderRadius =
      'current' in cutout &&
      cutout.current &&
      typeof borderRadius === 'undefined'
        ? parseInt(window.getComputedStyle(cutout.current).borderRadius, 10)
        : typeof borderRadius === 'string'
          ? Number(borderRadius.replace('px', ''))
          : typeof borderRadius === 'number'
            ? borderRadius * SPACING_BASE_VALUE
            : 0;

    const applyBorderRadius = assoc('borderRadius')(cutoutBorderRadius);

    const isDOMNode = typeof cutout === 'object' && 'current' in cutout;
    if (isDOMNode) {
      const box = convertRefToBox(cutout as React.MutableRefObject<Element>);
      if (box) {
        const convertedCutout = applyBorderRadius(relativeToViewport(box));
        convertedCutouts.push(convertedCutout);
      }
    } else {
      const convertedCutout = applyBorderRadius(
        relativeToViewport(cutout as Box)
      );
      convertedCutouts.push(convertedCutout);
    }
  });

  return convertedCutouts;
};

/**
 * Merges two sets of cutouts, ensuring that interactive cutouts contained within non-interactive cutouts
 * are excluded from the final list. This is because interactive cutouts are already being "cut out"
 * by the non-interactive cutouts they are within.
 * @param interactiveCutouts - An array of interactive cutouts represented as boxes.
 * @param nonInteractiveCutouts - An array of non-interactive cutouts represented as boxes.
 * @returns An array of cutouts to render, excluding interactive cutouts contained within non-interactive ones.
 */
const mergeAllCutouts = (
  interactiveCutouts: Box[],
  nonInteractiveCutouts: Box[]
): Box[] => {
  const cutoutsToAdd: Box[] = [...nonInteractiveCutouts];

  interactiveCutouts.forEach((interactiveCutout) => {
    let shouldAddCutout = true;
    nonInteractiveCutouts.forEach((nonInteractiveCutout) => {
      if (within(nonInteractiveCutout, interactiveCutout)) {
        shouldAddCutout = false;
      }
    });
    if (shouldAddCutout) {
      cutoutsToAdd.push(interactiveCutout);
    }
  });

  return cutoutsToAdd;
};

/**
 * # Mask
 *
 * The `Mask` component is a layer designed to be used behind Overlay components (e.g. Popover, Modal).
 *
 * ## Usage
 *
 * ```jsx
 * import {Mask} from '@packages/systems/spring';
 *
 * const Example = () => {
 *  const [isMaskOpen, setIsMaskOpen] = React.useState(false);
 *  const buttonRef = React.useRef(null);
 *
 *  return (
 *    <>
 *      <Button onClick={() => setIsMaskOpen(!isMaskOpen)} ref={buttonRef}>Toggle Mask</Button>
 *      <Mask
 *        interactiveCutouts={[{borderRadius: 1, cutout: buttonRef}]}
 *        isOpen={isMaskOpen}
 *        onClick={() => setIsMaskOpen(false)} />
 *    </>
 *  );
 * };
 * ```
 *
 * @component
 */
export const Mask = React.forwardRef<HTMLDivElement, MaskComponentProps>(
  (
    {
      anchorBorderRadius = 1,
      anchorCutoutRef,
      interactiveCutouts: interactiveCutoutsProp = emptyArray,
      isAnchorInteractive = false,
      isOpen = true,
      nonInteractiveCutouts: nonInteractiveCutoutsProp = emptyArray,
      onClick,
      shouldAllowPointerEvents = false,
      shouldEscapeViewport,
      style = emptyObject,
      type = 'popover',
      ...props
    }: MaskComponentProps,
    ref
  ) => {
    const id = React.useId();
    const maskId = `spring-mask-${id}`;

    // Force re-render when cutout elements resize / move
    const [, forceRender] = React.useState(false);

    const rafIdRef = React.useRef<number | null>(null);
    const scheduleRerender = React.useCallback(() => {
      if (rafIdRef.current != null) return;

      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null;
        forceRender((prev) => !prev);
      });
    }, []);

    const onResizeCutout = React.useCallback(() => {
      if (typeof window === 'undefined') return;
      scheduleRerender();
    }, [scheduleRerender]);

    const resizeObserver = useResizeObserver(onResizeCutout);

    // Collect all refs from cutouts for observation
    const collectCutoutRefs = React.useMemo(() => {
      const refs: React.MutableRefObject<Element | null>[] = [];

      if (anchorCutoutRef && 'current' in anchorCutoutRef) {
        refs.push(anchorCutoutRef);
      }

      interactiveCutoutsProp.forEach((cutoutDef) => {
        if (cutoutDef && 'current' in cutoutDef.cutout) {
          refs.push(cutoutDef.cutout);
        }
      });

      nonInteractiveCutoutsProp.forEach((cutoutDef) => {
        if (cutoutDef && 'current' in cutoutDef.cutout) {
          refs.push(cutoutDef.cutout);
        }
      });

      return refs;
    }, [anchorCutoutRef, interactiveCutoutsProp, nonInteractiveCutoutsProp]);

    // Observe all cutout refs for size changes
    React.useEffect(() => {
      resizeObserver.disconnect();

      collectCutoutRefs.forEach((cutoutRef) => {
        if (cutoutRef.current) {
          resizeObserver.observe(cutoutRef.current as HTMLElement);
        }
      });

      return () => resizeObserver.disconnect();
    }, [collectCutoutRefs, resizeObserver]);

    // Also re-render on scroll/resize because ResizeObserver won't fire for pure position changes
    React.useEffect(() => {
      if (typeof window === 'undefined') return;
      if (!isOpen) return;

      /**
       * Attach at window (capture) to catch nested scrolls.
       *
       * We may run into an issue where events don't bubble up to the window.
       * Since Viewport isn't a real DOM node, we can't reliably bind to the exact container the Mask covers.
       */
      window.addEventListener('scroll', scheduleRerender, true);
      window.addEventListener('resize', scheduleRerender);

      return () => {
        window.removeEventListener('scroll', scheduleRerender, true);
        window.removeEventListener('resize', scheduleRerender);
      };
    }, [isOpen, scheduleRerender]);

    // Cleanup any pending RAF on unmount
    React.useEffect(() => {
      return () => {
        if (rafIdRef.current != null && typeof window !== 'undefined') {
          window.cancelAnimationFrame(rafIdRef.current);
        }
      };
    }, []);

    const overlayContext = useOverlay();

    /**
     * The following type assertions are a temporary workaround to suppress a TypeScript error
     * by assuming Overlay returns a Box with x & y. This is necessary due to differences between
     * Webflow's Box implementation and the HTML Rect object. In the future, a comprehensive
     * migration of Box implementations may resolve this issue.
     */
    const overlayContextDimensions = overlayContext?.[0]?.box as Box;
    const viewportContextDimensions = useViewport() as Box;
    let viewport = shouldEscapeViewport
      ? overlayContextDimensions
      : viewportContextDimensions;

    /**
     * If no Overlay or Viewport context exist, we'll use the window's dimensions as the viewport.
     */
    if (
      viewport.height === 0 &&
      viewport.width === 0 &&
      typeof window !== 'undefined'
    ) {
      viewport = {
        top: 0,
        left: 0,
        height: window.innerHeight,
        width: window.innerWidth,
      };
    }

    const isTransparent = type === 'transparent';

    /**
     * If the Mask is transparent and pointer events are allowed, we don't need to render anything.
     */
    if (!isOpen || (shouldAllowPointerEvents && isTransparent)) {
      return null;
    }

    const interactiveCutouts = [...interactiveCutoutsProp];
    const nonInteractiveCutouts = [...nonInteractiveCutoutsProp];

    if (anchorCutoutRef) {
      const anchorCutout = {
        borderRadius: anchorBorderRadius,
        cutout: anchorCutoutRef,
      };

      if (isAnchorInteractive) {
        interactiveCutouts.push(anchorCutout);
      } else {
        nonInteractiveCutouts.push(anchorCutout);
      }
    }

    const fill = isTransparent
      ? 'transparent'
      : type === 'popover'
        ? tokens.colors.uiPopoverBackdrop
        : tokens.colors.uiModalBackdrop;

    const svgWrapperFill = isTransparent
      ? 'transparent'
      : type === 'popover'
        ? 'popover'
        : 'default';

    const relativeToViewport = relative(viewport);

    const convertedInteractiveCutouts = convertCutouts(
      interactiveCutouts,
      relativeToViewport
    );

    const convertedNonInteractiveCutouts = convertCutouts(
      nonInteractiveCutouts,
      relativeToViewport
    );

    const mergedCutouts = mergeAllCutouts(
      convertedInteractiveCutouts,
      convertedNonInteractiveCutouts
    );

    const handleOnClick = (event: React.WheelEvent<HTMLElement>) => {
      onClick && onClick(event);
    };

    const handleOnWheel = (event: React.WheelEvent<SVGSVGElement>) => {
      if (!shouldAllowPointerEvents) {
        // Prevent scrolling when the Mask if pointer events are disallowed
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const {top, left, height, width} = viewport;
    const maskStyle = {
      height,
      width,
      top,
      left,
      ...style,
    };

    return (
      <View
        id={maskId}
        onClick={handleOnClick}
        pointerEvents="none"
        position="fixed"
        ref={ref}
        style={maskStyle}
        {...props}
      >
        <SVGWrapper
          fill={svgWrapperFill}
          onWheel={handleOnWheel}
          style={{height, width, top, left}}
        >
          <path
            d={createCutout(relativeToViewport(viewport), ...mergedCutouts)}
            fill={fill}
            fillRule="evenodd"
            pointerEvents="none"
          />
          <path
            d={createCutout(
              relativeToViewport(viewport),
              ...convertedInteractiveCutouts
            )}
            fill="transparent"
            fillRule="evenodd"
            pointerEvents={shouldAllowPointerEvents ? 'none' : 'auto'}
          />
        </SVGWrapper>
      </View>
    );
  }
);

Mask.displayName = 'Mask';

export default Mask;
